import React, { useContext, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext';
import { FLAG_REASONS } from '../admin/flagReasons';

// Superadmin-only: mark a listing as problematic; it shows up under Admin → Listing flags.
function FlagListingButton({ listing, className = '' }) {
  const { role } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState('');
  const [duplicateOf, setDuplicateOf] = useState('');
  const [state, setState] = useState({ saving: false, error: '', flagged: false });

  if (role !== 'superadmin' || !listing || !listing.uuid) return null;

  const toggle = (key) => setReasons((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));

  const submit = async () => {
    setState({ saving: true, error: '', flagged: false });
    try {
      const response = await fetch('/api/admin/listing-flags', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing: listing.uuid, reasons, note, duplicateOf: reasons.includes('duplicate') ? duplicateOf : '' }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || `Request failed (${response.status})`);
      setState({ saving: false, error: '', flagged: true });
      setOpen(false);
      setReasons([]);
      setNote('');
      setDuplicateOf('');
    } catch (error) {
      setState({ saving: false, error: error.message, flagged: false });
    }
  };

  return (
    <span className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        className={`border rounded-full border-black px-2 py-1 text-sm ${state.flagged ? 'bg-red-100' : 'bg-white hover:bg-red-50'}`}
        title="Flag a problem with this listing"
      >
        {state.flagged ? '⚑ Flagged' : '⚑ Flag'}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-1 w-72 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-left text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-medium mb-2">What’s wrong?</div>
          <div className="flex flex-wrap gap-1 mb-2">
            {FLAG_REASONS.map(([key, label]) => (
              <button
                type="button"
                key={key}
                onClick={() => toggle(key)}
                className={`px-2 py-0.5 text-xs rounded-full border ${reasons.includes(key) ? 'bg-red-600 text-white border-red-600' : 'border-gray-300 hover:bg-gray-50'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {reasons.includes('duplicate') && (
            <input
              value={duplicateOf}
              onChange={(e) => setDuplicateOf(e.target.value)}
              placeholder="Duplicate of (link or UUID, optional)"
              className="w-full border border-gray-300 rounded px-2 py-1 mb-2"
            />
          )}
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && reasons.length) submit(); }}
            placeholder="Note, e.g. show is 8–10 PM"
            className="w-full border border-gray-300 rounded px-2 py-1 mb-2"
          />
          {state.error && <p className="text-red-700 text-xs mb-2">{state.error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-2 py-1 rounded border border-gray-300">Cancel</button>
            <button
              type="button"
              disabled={!reasons.length || state.saving}
              onClick={submit}
              className="px-2 py-1 rounded bg-red-600 text-white disabled:opacity-40"
            >
              {state.saving ? 'Flagging…' : 'Flag'}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

export default FlagListingButton;
